import React, { useState, useEffect } from "react";
import { Lock, Loader2, Globe } from "lucide-react";
import { useSelector } from "react-redux";
import { getUserById } from "../../services/authService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getStaticUrl } from "../../config/domains";

import WebsiteManager from "./WebsiteTranslator/WebsiteManager";
import LanguageSettings from "./WebsiteTranslator/LanguageSettings";
import EmbedCodeBlock from "./WebsiteTranslator/EmbedCodeBlock";
import { getWebsiteConfig, registerWebsite, updateLanguageSettings, regenerateWebsiteApiKey } from "../../services/translationService";

const WebsiteTranslation = () => {
    const { token } = useSelector((state) => state.auth);

    const [checkingSubscription, setCheckingSubscription] = useState(true);
    const [userDetails, setUserDetails] = useState(null);

    const [websiteConfig, setWebsiteConfig] = useState(null);
    const [isProcessingConfig, setIsProcessingConfig] = useState(false);

    useEffect(() => {
        const getUserDetails = async () => {
            if (!token) return;
            const response = await getUserById(token);
            if (response) {
                setUserDetails(response);
            }
        };
        getUserDetails();
    }, [token]);

    useEffect(() => {
        const loadConfigData = async () => {
            if (!token) {
                setCheckingSubscription(false);
                return;
            }

            try {
                setCheckingSubscription(true);

                try {
                    const config = await getWebsiteConfig(token);
                    setWebsiteConfig(config);
                } catch (e) {}

            } catch (error) {
                console.error(error);
            } finally {
                setCheckingSubscription(false);
            }
        };

        loadConfigData();
    }, [token]);

    const checkTokenStatus = () => {
        if (!userDetails) {
            return { expired: false, exhausted: true };
        }
        const tokensAvailable = userDetails.tokens > 0;
        const exhausted = !tokensAvailable;
        let expired = false;
        if (userDetails.tokenExpiration) {
            const expirationDate = new Date(userDetails.tokenExpiration);
            const currentDate = new Date();
            expired = currentDate > expirationDate;
        }
        return { expired, exhausted };
    };

    const handleSaveWebsiteDomain = async (domain) => {
        setIsProcessingConfig(true);
        const result = await registerWebsite(domain, token);
        if (result) {
            setWebsiteConfig(result);
            toast.success("Website domain registered successfully!");
        } else {
            toast.error("Failed to register website domain.");
        }
        setIsProcessingConfig(false);
    };

    const handleLanguageUpdate = async (defaultLang, targetLangs) => {
        setIsProcessingConfig(true);
        const result = await updateLanguageSettings({
            defaultLanguage: defaultLang,
            targetLanguages: targetLangs
        }, token);

        if (result) {
            setWebsiteConfig(result);
            toast.success("Language settings updated successfully!");
        } else {
            toast.error("Failed to update language settings.");
        }
        setIsProcessingConfig(false);
    };

    const handleRegenerateApiKey = async () => {
        if (!window.confirm("Are you sure? This will instantly break translation on your website until you copy the new code.")) {
            return;
        }

        setIsProcessingConfig(true);
        const result = await regenerateWebsiteApiKey(websiteConfig.domain, token);
        if (result) {
            setWebsiteConfig(result);
            toast.success("Security key regenerated! Please update your website code.");
        } else {
            toast.error("Failed to regenerate API key.");
        }
        setIsProcessingConfig(false);
    };

    if (checkingSubscription) {
        return (
            <div className="p-6 flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const { expired: tokensExpired, exhausted: tokensExhausted } = checkTokenStatus();
    const shouldLockFeature = tokensExpired || tokensExhausted;

    if (shouldLockFeature) {
        return (
            <div className="p-6 flex items-center justify-center min-h-[600px]">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 max-w-xl w-full text-center">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Lock className="w-10 h-10 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        Premium feature locked
                    </h2>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                        This feature is available for our active subscribers. You’re welcome to try our free, slower models in the playground.
                    </p>
                    <button
                        onClick={() => window.open(getStaticUrl('/playground'), '_blank', 'noopener,noreferrer')}
                        className="w-full bg-secondary hover:bg-teal-800 text-white font-medium py-3 px-6 rounded-xl transition-colors duration-200"
                    >
                        Use free playground
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-[#1E6C56] to-[#2a8a6e] rounded-2xl p-8 text-white shadow-md relative overflow-hidden">
                    <div className="relative z-10 max-w-2xl">
                        <h2 className="text-2xl md:text-3xl font-black mb-3 text-white flex items-center gap-3">
                            <Globe className="text-[#FFB800] w-8 h-8" />
                            Website Auto-Translator
                        </h2>
                        <p className="text-white/80 text-lg">
                            Add our smart widget to your site and instantly translate your entire website into Yoruba, Swahili, and Nigerian Pidgin. Just register your domain and paste the code.
                        </p>
                    </div>
                </div>

                <WebsiteManager
                    websiteConfig={websiteConfig}
                    onSave={handleSaveWebsiteDomain}
                    isProcessing={isProcessingConfig}
                />

                <LanguageSettings
                    websiteConfig={websiteConfig}
                    onLanguageUpdate={handleLanguageUpdate}
                    isProcessing={isProcessingConfig}
                />

                <EmbedCodeBlock
                    websiteConfig={websiteConfig}
                    onRegenerateApiKey={handleRegenerateApiKey}
                    isProcessing={isProcessingConfig}
                />
            </div>
        </div>
    );
};

export default WebsiteTranslation;
